/* variant-model-selector-ac.js  |  AquaCurve Variant Model Selector
 * Version: 2026-01-06-v16.1
 *
 * Keeps:
 *  - price refresh + media switch (NO fullscreen)
 *  - config card active highlight sync
 *  - correct variant submitted to /cart/add (cart drawer + buy it now OK)
 *  - preserves all URL params (UTM etc.) when setting variant
 *
 * v16.1 (based on your V16) improvements:
 *  - Fix "hero jumps to config variant #1 image after a few seconds" by temporarily disabling
 *    theme's active-variant-scroll auto media switching on load, then restoring.
 *  - More robust hero lock: works even if sectionId is empty / thumbnails id differs.
 *  - Lightweight MutationObserver with debounce to reduce performance impact.
 */
(() => {
  'use strict';

  const VERSION = '2026-01-06-v16.1';
  window.__AC_VMS_VERSION__ = VERSION;

  /* --------------------------
   * User-tunable defaults
   * -------------------------- */
  const DEFAULT_HERO_THUMB_INDEX = 0;     // 0 = first image, 1 = second image
  const PREFERRED_CONFIG_CARD_INDEX = 2;  // 2 = third configuration card (0-based)

  // New: how long to suppress theme auto media switching on initial load (ms)
  // (This is what causes the "<4 seconds jump to config #1 image".)
  const HERO_LOCK_WINDOW_MS = 6500;

  const LOG = (...args) => console.log('[AC VMS]', ...args);
  const WARN = (...args) => console.warn('[AC VMS]', ...args);

  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  let userInteracted = false;
  let bootedOnce = false;
  let firstVariantChangeHandled = false;

  function closestProductScope(node) {
    if (!node) return document;
    return (
      node.closest('[id^="MainProduct-"]') ||
      node.closest('[id^="product-info-"]') ||
      node.closest('.product') ||
      document
    );
  }

  function findPicker(scope) {
    return qs('variant-selects', scope) || qs('variant-radios', scope) || null;
  }

  function getSectionIdFromPicker(picker) {
    if (!picker) return '';
    const id = picker.id || '';
    const m = id.match(/variant-(?:selects|radios)-(.+)$/);
    return (m && m[1]) ? m[1] : '';
  }

  function getUrlObj() {
    try { return new URL(location.href); } catch (e) { return null; }
  }

  function getVariantIdFromUrl() {
    const u = getUrlObj();
    if (!u) return '';
    return u.searchParams.get('variant') || '';
  }

  function hasVariantInUrl() {
    return !!getVariantIdFromUrl();
  }

  // Important: preserve ALL query params (UTM etc.) and only set/replace variant
  function setVariantIdToUrl(variantId) {
    const u = getUrlObj();
    if (!u) return false;
    u.searchParams.set('variant', String(variantId));
    history.replaceState({}, '', u.toString());
    return true;
  }

  // Detect "auto variant" situations (theme preview/cache bust) where we should override to preferred config
  function isAutoVariantContext() {
    const u = getUrlObj();
    if (!u) return false;
    if (u.searchParams.has('cachebust')) return true;
    if (u.searchParams.has('preview_theme_id')) return true;
    return false;
  }

  function dispatchNativeChange(el) {
    if (!el) return;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* -----------------------------------------
   * Sync ALL /cart/add inputs to target variant
   * ----------------------------------------- */
  function findAllIdInputs(scope, sectionId) {
    const inputs = new Set();

    qsa('form[action^="/cart/add"] input[name="id"]', scope).forEach(i => inputs.add(i));
    qsa('product-form form input[name="id"]', scope).forEach(i => inputs.add(i));

    if (sectionId) {
      qsa(`input[name="id"][form*="${CSS.escape(sectionId)}"]`, document).forEach(i => inputs.add(i));
      qsa(`form[id*="${CSS.escape(sectionId)}"][action^="/cart/add"] input[name="id"]`, document).forEach(i => inputs.add(i));
      qsa(`[id*="${CSS.escape(sectionId)}"] form[action^="/cart/add"] input[name="id"]`, document).forEach(i => inputs.add(i));
    }

    // PDP fallback
    qsa('form[action^="/cart/add"] input[name="id"]', document).forEach(i => inputs.add(i));

    return Array.from(inputs);
  }

  function syncAllVariantId(scope, sectionId, variantId) {
    const inputs = findAllIdInputs(scope, sectionId);
    if (!inputs.length) return { ok: false, count: 0 };

    inputs.forEach((input) => {
      input.value = String(variantId);
      dispatchNativeChange(input);
      input.setAttribute('data-ac-variant-id', String(variantId));
    });

    try {
      const ev = new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variantId: String(variantId), source: 'ac-vms' }
      });
      (scope || document).dispatchEvent(ev);
      document.dispatchEvent(ev);
    } catch (e) {}

    return { ok: true, count: inputs.length };
  }

  async function fetchVariant(variantId) {
    const res = await fetch(`/variants/${variantId}.js`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Failed to fetch variant: ${variantId}`);
    return res.json();
  }

  async function forceSectionRerenderByVariant(scope, sectionId, variantId) {
    if (!sectionId) return false;

    try {
      const url = new URL(location.href);
      url.searchParams.set('variant', String(variantId));
      url.searchParams.set('section_id', sectionId);

      const res = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!res.ok) return false;

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const priceSelector = `#price-${sectionId}`;
      const newPrice = qs(priceSelector, doc);
      const curPrice = qs(priceSelector, document);
      if (newPrice && curPrice) curPrice.replaceWith(newPrice);

      return true;
    } catch (e) {
      WARN('section rerender failed', e);
      return false;
    }
  }

  function getFeaturedMediaIdFromVariant(v) {
    const mediaId =
      (v && v.featured_media && v.featured_media.id) ||
      (v && v.featured_image && v.featured_image.id) ||
      null;
    return mediaId ? String(mediaId) : '';
  }

  // Switch media without triggering fullscreen (via media-gallery or thumbnail click)
  function switchMediaNoFullscreen(scope, sectionId, mediaId) {
    if (!sectionId || !mediaId) return { ok: false, reason: 'missing sectionId/mediaId' };

    const targetMediaKey = `${sectionId}-${mediaId}`;

    const gallery =
      qs('media-gallery', scope) ||
      qs('media-gallery', document) ||
      qs('[id^="MediaGallery-"]', scope) ||
      qs('[id^="MediaGallery-"]', document);

    if (gallery && typeof gallery.setActiveMedia === 'function') {
      try {
        gallery.setActiveMedia(targetMediaKey, true);
        return { ok: true, via: 'media-gallery.setActiveMedia' };
      } catch (e) {}
    }

    const thumbRoot =
      qs(`#Slider-Thumbnails-${CSS.escape(sectionId)}`, document) ||
      qs('[id^="Slider-Thumbnails-"]', scope) ||
      null;

    const thumbLiSelector = `[data-media-id="${CSS.escape(targetMediaKey)}"]`;
    let thumbBtn = null;

    if (thumbRoot) {
      const li = qs(thumbLiSelector, thumbRoot);
      if (li) thumbBtn = qs('button', li) || qs('a', li) || null;
    }

    if (!thumbBtn) {
      const li = qs(thumbLiSelector, scope) || qs(thumbLiSelector, document);
      if (li) thumbBtn = qs('button', li) || qs('a', li) || null;
    }

    if (thumbBtn) {
      try {
        thumbBtn.click();
        return { ok: true, via: 'thumbnail-click' };
      } catch (e) {
        return { ok: false, reason: 'thumbnail click failed' };
      }
    }

    return { ok: false, reason: 'thumbnail not found' };
  }

  /* -----------------------------
   * Active highlight sync for cards
   * ----------------------------- */
  function getCardGroupRoot(cardEl) {
    return (
      cardEl.closest('.variant-models_grid') ||
      cardEl.closest('.variant-models') ||
      cardEl.closest('.bundle-section') ||
      cardEl.closest('[data-variant-models]') ||
      cardEl.closest('[data-config-group]') ||
      cardEl.parentElement ||
      document
    );
  }

  function clearActiveState(groupRoot) {
    const cards = qsa('[data-variant-id]', groupRoot);
    cards.forEach((c) => {
      c.classList.remove('is-active', 'active', 'selected');
      c.setAttribute('aria-pressed', 'false');
      c.setAttribute('aria-current', 'false');
      if (c.hasAttribute('aria-checked')) c.setAttribute('aria-checked', 'false');
      if (c.hasAttribute('data-active')) c.removeAttribute('data-active');
    });
  }

  function setActiveState(cardEl) {
    const groupRoot = getCardGroupRoot(cardEl);
    clearActiveState(groupRoot);

    cardEl.classList.add('is-active');
    cardEl.setAttribute('aria-pressed', 'true');
    cardEl.setAttribute('aria-current', 'true');
    if (cardEl.hasAttribute('aria-checked')) cardEl.setAttribute('aria-checked', 'true');
    cardEl.setAttribute('data-active', 'true');

    return true;
  }

  function syncActiveFromUrl(scope = document) {
    const vid = getVariantIdFromUrl();
    if (!vid) return false;

    const card =
      qs(`[data-variant-id="${CSS.escape(String(vid))}"]`, scope) ||
      qs(`[data-variant-id="${CSS.escape(String(vid))}"]`, document);

    if (!card) return false;
    setActiveState(card);
    return true;
  }

  /* -----------------------------------------
   * Submit protection
   * ----------------------------------------- */
  function wireSubmitProtection() {
    if (document.__acVmsSubmitBound) return;
    document.__acVmsSubmitBound = true;

    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      const action = (form.getAttribute('action') || '').toLowerCase();
      if (!action.includes('/cart/add')) return;

      const vid = getVariantIdFromUrl();
      if (!vid) return;

      const scope = closestProductScope(form);
      const picker = findPicker(scope);
      const sectionId = getSectionIdFromPicker(picker) || '';

      const input = qs('input[name="id"]', form);
      if (input) {
        input.value = String(vid);
        dispatchNativeChange(input);
      }

      syncAllVariantId(scope, sectionId, vid);
      LOG('submit protection applied', { vid, formId: form.id || '(no id)' });
    }, true);
  }

  /* -----------------------------------------
   * v16.1: suppress theme auto media switching at load
   * ----------------------------------------- */
  function findMediaGallery(scope) {
    return (
      qs('media-gallery', scope) ||
      qs('media-gallery', document) ||
      qs('[id^="MediaGallery-"]', scope) ||
      qs('[id^="MediaGallery-"]', document) ||
      null
    );
  }

  function temporarilyDisableActiveVariantScroll(scope, ms, reason) {
    const mg = findMediaGallery(scope);
    if (!mg) return () => {};

    const keys = [
      'data-active-variant-scroll',
      'active-variant-scroll',
      'data-active-variant-scroll-on-change',
      'active-variant-scroll-on-change',
      'data-active-variant-scroll-change',
      'active-variant-scroll-change'
    ];

    const prev = {};
    keys.forEach(k => { prev[k] = mg.getAttribute(k); });

    // Force OFF
    mg.setAttribute('data-active-variant-scroll', 'false');
    mg.setAttribute('data-active-variant-scroll-on-change', 'false');
    mg.setAttribute('active-variant-scroll', 'false');
    mg.setAttribute('active-variant-scroll-on-change', 'false');

    LOG('active-variant-scroll temporarily disabled', { ms, reason });

    const t = setTimeout(() => {
      keys.forEach(k => {
        const v = prev[k];
        if (v === null || typeof v === 'undefined') mg.removeAttribute(k);
        else mg.setAttribute(k, v);
      });
      LOG('active-variant-scroll restored', { reason });
    }, Math.max(0, ms | 0));

    return () => clearTimeout(t);
  }

  /* -----------------------------------------
   * v16.1: lock hero media to scene thumbnail (robust)
   * ----------------------------------------- */
  function findThumbnailRoot(scope, sectionId) {
    // Primary (sectionId based)
    if (sectionId) {
      const r1 = qs(`#Slider-Thumbnails-${CSS.escape(sectionId)}`, document);
      if (r1) return r1;
    }

    // Fallbacks: any thumbnail slider in product scope
    const r2 = qs('[id^="Slider-Thumbnails-"]', scope);
    if (r2) return r2;

    // Extra fallback: any thumbnails list in DOM
    const r3 = qs('[id^="Slider-Thumbnails-"]', document);
    if (r3) return r3;

    return null;
  }

  function lockHeroToScene(scope, sectionId, thumbIndex, reason) {
    if (userInteracted) return false;

    const root = findThumbnailRoot(scope, sectionId);
    if (!root) return false;

    const items = qsa('[data-media-id]', root);
    if (!items.length) return false;

    const idx = Math.max(0, Math.min(items.length - 1, thumbIndex | 0));
    const target = items[idx];
    const btn = target ? (qs('button', target) || qs('a', target)) : null;
    if (!btn) return false;

    // Avoid modal opener paths (safety)
    const isModal = btn.closest('modal-opener,[data-modal-opener],.product__modal-opener');
    if (isModal) return false;

    try {
      btn.click(); // thumbnail click, NOT fullscreen
      LOG('hero locked to scene', { sectionId, thumbIndex: idx, reason });
      return true;
    } catch (e) {
      return false;
    }
  }

  /* -----------------------------------------
   * v16: default preferred config selection
   * - Override only if:
   *   A) URL has no variant
   *   OR
   *   B) URL has auto-variant context (cachebust/preview)
   * - DOES NOT intentionally switch media (scene stays)
   * ----------------------------------------- */
  async function defaultSelectPreferredConfig(scope, sectionId) {
    const shouldOverride = (!hasVariantInUrl()) || isAutoVariantContext();
    if (!shouldOverride) return false;

    const configCards = qsa('[data-variant-id][data-opt2]', scope);
    if (configCards.length < 3) return false;

    const card = configCards[PREFERRED_CONFIG_CARD_INDEX] || null;
    if (!card) return false;

    const variantId = card.getAttribute('data-variant-id');
    if (!variantId) return false;

    setActiveState(card);
    setVariantIdToUrl(variantId);
    syncAllVariantId(scope, sectionId, variantId);

    // Update price to match preferred config
    const priceOk = await forceSectionRerenderByVariant(scope, sectionId, variantId);

    LOG('default preferred config applied', { variantId, priceOk, idx: PREFERRED_CONFIG_CARD_INDEX });
    return true;
  }

  function wireVariantCards() {
    qsa('[data-variant-id]').forEach((el) => {
      if (el.__acVmsBound) return;
      el.__acVmsBound = true;

      el.addEventListener('click', async () => {
        userInteracted = true;

        try {
          const variantId = el.getAttribute('data-variant-id');
          if (!variantId) return;

          setActiveState(el);

          const scope = closestProductScope(el);
          const picker = findPicker(scope);

          const sectionId =
            getSectionIdFromPicker(picker) ||
            (() => {
              const any = qs('[id*="__main"]', scope) || qs('[id*="__main"]', document);
              if (!any || !any.id) return '';
              const m = any.id.match(/(template--\d+__main)/);
              return (m && m[1]) ? m[1] : '';
            })();

          const urlOk = setVariantIdToUrl(variantId);
          const idSync = syncAllVariantId(scope, sectionId, variantId);

          LOG('click', { variantId, sectionId, urlOk, idSync });

          const priceOk = await forceSectionRerenderByVariant(scope, sectionId, variantId);

          const v = await fetchVariant(variantId);
          const mediaId = getFeaturedMediaIdFromVariant(v);

          let mediaOk = false;
          let mediaVia = '';
          if (mediaId) {
            const r = switchMediaNoFullscreen(scope, sectionId, mediaId);
            mediaOk = !!r.ok;
            mediaVia = r.via || r.reason || '';
          }

          LOG('refresh done', { variantId, priceOk, mediaOk, mediaVia, mediaId });
        } catch (e) {
          WARN('variant-card handler error', e);
        }
      }, true);
    });
  }

  async function boot() {
    if (bootedOnce) return;
    bootedOnce = true;

    wireVariantCards();
    wireSubmitProtection();

    const scope = closestProductScope(qs('[data-variant-id]') || document.body);
    const picker = findPicker(scope);
    const sectionId =
      getSectionIdFromPicker(picker) ||
      (() => {
        const any = qs('[id*="__main"]', scope) || qs('[id*="__main"]', document);
        if (!any || !any.id) return '';
        const m = any.id.match(/(template--\d+__main)/);
        return (m && m[1]) ? m[1] : '';
      })();

    // v16.1: Stop theme from auto-switching gallery to variant media during initial boot window,
    // then we lock the hero to scene image + apply preferred config.
    const cancelRestore = temporarilyDisableActiveVariantScroll(scope, HERO_LOCK_WINDOW_MS, 'boot');

    // 1) Lock hero to scene ASAP (before any theme variant media alignment)
    lockHeroToScene(scope, sectionId, DEFAULT_HERO_THUMB_INDEX, 'boot-immediate');

    // 2) Apply preferred config default (override safe for cachebust/preview or no variant)
    //    NOTE: This updates URL variant + price + /cart/add id inputs. It does NOT intentionally change media.
    await defaultSelectPreferredConfig(scope, sectionId);

    // 3) Lock hero again at strategic moments to beat delayed theme work (WITHOUT heavy polling)
    requestAnimationFrame(() => {
      if (!userInteracted) lockHeroToScene(scope, sectionId, DEFAULT_HERO_THUMB_INDEX, 'boot-raf');
    });
    setTimeout(() => {
      if (!userInteracted) lockHeroToScene(scope, sectionId, DEFAULT_HERO_THUMB_INDEX, 'boot-700ms');
    }, 700);
    setTimeout(() => {
      if (!userInteracted) lockHeroToScene(scope, sectionId, DEFAULT_HERO_THUMB_INDEX, 'boot-1800ms');
    }, 1800);
    setTimeout(() => {
      if (!userInteracted) lockHeroToScene(scope, sectionId, DEFAULT_HERO_THUMB_INDEX, 'boot-3800ms');
    }, 3800);

    // 4) On first variant:change (theme or our sync), lock hero once more
    document.addEventListener('variant:change', () => {
      if (firstVariantChangeHandled) return;
      firstVariantChangeHandled = true;
      setTimeout(() => {
        if (!userInteracted) lockHeroToScene(scope, sectionId, DEFAULT_HERO_THUMB_INDEX, 'first-variant:change');
      }, 220);
    }, true);

    // 5) Keep cards bound & highlight synced when DOM changes (debounced, lightweight)
    let moTimer = null;
    const mo = new MutationObserver(() => {
      if (moTimer) return;
      moTimer = setTimeout(() => {
        moTimer = null;
        wireVariantCards();
        syncActiveFromUrl(document);
      }, 180);
    });
    mo.observe(document.body || document.documentElement, { childList: true, subtree: true });

    window.addEventListener('popstate', () => {
      syncActiveFromUrl(document);
    });

    // Restore hook already scheduled; keep reference to avoid lint unused
    void cancelRestore;

    LOG('loaded', VERSION);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
